/**
 * Flickity Mousewheel Extension
 * Adds mousewheel/trackpad scrolling support to Flickity carousels
 */

(function() {
    'use strict';

    // Check if Flickity is available
    if (typeof window.themeVendor === 'undefined' || !window.themeVendor.Flickity) {
        console.warn('Flickity Mousewheel Extension: Flickity not found');
        return;
    }

    const Flickity = window.themeVendor.Flickity;

    // Default options for mousewheel
    const defaultOptions = {
        mousewheel: false,
        mousewheelSensitivity: 1,
        mousewheelPreventOnTarget: null
    };

    // Extend Flickity defaults
    Flickity.defaults = Object.assign({}, Flickity.defaults, defaultOptions);

    // Store original create method
    const originalCreate = Flickity.prototype._create;

    // Extend the _create method
    Flickity.prototype._create = function() {
        // Call original create method
        originalCreate.call(this);

        // Initialize mousewheel if enabled
        if (this.options.mousewheel) {
            this._initMousewheel();
        }
    };

    // Mousewheel initialization
    Flickity.prototype._initMousewheel = function() {
        this.mousewheelData = {
            isScrolling: false,
            scrollTimeout: null,
            lastScrollTime: 0,
            lastGestureType: null,
            gestureCooldown: 0
        };

        // Bind mousewheel event
        this.element.addEventListener('wheel', this._handleMousewheel.bind(this), { passive: false });
        
        // Clean up on destroy
        this.on('destroy', this._destroyMousewheel.bind(this));
    };

    // Handle mousewheel events
    Flickity.prototype._handleMousewheel = function(event) {
        // Don't handle if carousel is not active
        if (!this.isActive) return;

        // Check if we should prevent default behavior
        if (this._shouldPreventMousewheel(event)) {
            event.preventDefault();
        }

        // Throttle scroll events
        const now = Date.now();
        if (now - this.mousewheelData.lastScrollTime < 30) { // Reduced throttle for better responsiveness
            return;
        }
        this.mousewheelData.lastScrollTime = now;

        // Apply sensitivity
        const sensitivity = this.options.mousewheelSensitivity || 1;

        // For horizontal carousels, handle both mousewheel and touchpad gestures
        const isHorizontal = !this.options.vertical;
        
        if (isHorizontal) {
            const horizontalDelta = event.deltaX;
            const verticalDelta = event.deltaY || event.wheelDelta;
            
            // Determine the primary gesture direction
            const absHorizontal = Math.abs(horizontalDelta);
            const absVertical = Math.abs(verticalDelta);
            
            // Check if we're in a gesture cooldown period
            if (now - this.mousewheelData.gestureCooldown < 50) { // Reduced cooldown
                return;
            }
            
            // Detect if this is likely a touchpad gesture (both deltas present)
            const isTouchpadGesture = absHorizontal > 0 && absVertical > 0;
            
            // For touchpad gestures, prioritize horizontal movement
            if (isTouchpadGesture && absHorizontal > 0) {
                // Touchpad horizontal gesture - much more sensitive
                const adjustedDelta = absHorizontal * sensitivity * 2; // Double sensitivity for touchpad
                
                if (adjustedDelta >= 5) { // Lower threshold for touchpad
                    // Prevent mixed gesture handling
                    this.mousewheelData.gestureCooldown = now;
                    this.mousewheelData.lastGestureType = 'touchpad-horizontal';
                    
                    // Navigate carousel based on horizontal scroll direction
                    if (horizontalDelta > 0) {
                        this.next(); // Scroll right = go to next slide
                    } else {
                        this.previous(); // Scroll left = go to previous slide
                    }
                    event.preventDefault();
                }
            } 
            // For pure horizontal gestures (like horizontal mousewheel)
            else if (absHorizontal > absVertical && absHorizontal > 0) {
                const adjustedDelta = absHorizontal * sensitivity;
                
                if (adjustedDelta >= 10) {
                    this.mousewheelData.gestureCooldown = now;
                    this.mousewheelData.lastGestureType = 'horizontal';
                    
                    if (horizontalDelta > 0) {
                        this.next();
                    } else {
                        this.previous();
                    }
                    event.preventDefault();
                }
            } 
            // For pure vertical gestures (traditional mousewheel)
            else if (absVertical > absHorizontal && absVertical > 0 && !isTouchpadGesture) {
                const adjustedDelta = absVertical * sensitivity;
                
                if (adjustedDelta >= 10) {
                    this.mousewheelData.gestureCooldown = now;
                    this.mousewheelData.lastGestureType = 'vertical';
                    
                    if (verticalDelta > 0) {
                        this.next();
                    } else {
                        this.previous();
                    }
                    event.preventDefault();
                }
            }
        } else {
            // For vertical carousels, use horizontal mousewheel (deltaX) to navigate
            const horizontalDelta = event.deltaX;
            
            if (Math.abs(horizontalDelta) > 0) {
                const adjustedDelta = Math.abs(horizontalDelta) * sensitivity;
                
                if (adjustedDelta >= 10) {
                    if (horizontalDelta > 0) {
                        this.next();
                    } else {
                        this.previous();
                    }
                    event.preventDefault();
                }
            }
        }
    };

    // Check if mousewheel should be prevented
    Flickity.prototype._shouldPreventMousewheel = function(event) {
        const preventTarget = this.options.mousewheelPreventOnTarget;
        
        if (preventTarget) {
            const target = event.target;
            const preventElement = typeof preventTarget === 'string' ? 
                this.element.querySelector(preventTarget) : 
                preventTarget;
            
            if (preventElement && (preventElement === target || preventElement.contains(target))) {
                return false;
            }
        }

        return true;
    };

    // Clean up mousewheel
    Flickity.prototype._destroyMousewheel = function() {
        if (this.element) {
            this.element.removeEventListener('wheel', this._handleMousewheel.bind(this));
        }
        this.mousewheelData = null;
    };

    // Add mousewheel option to existing carousels
    Flickity.prototype.enableMousewheel = function() {
        this.options.mousewheel = true;
        this._initMousewheel();
    };

    Flickity.prototype.disableMousewheel = function() {
        this.options.mousewheel = false;
        this._destroyMousewheel();
    };

    // Extend the existing flickity-carousel custom element
    if (typeof window.customElements !== 'undefined') {
        const FlickityCarousel = window.customElements.get('flickity-carousel');
        
        if (FlickityCarousel) {
            // Store original initCarousel method
            const originalInitCarousel = FlickityCarousel.prototype.initCarousel;
            
            // Extend initCarousel to include mousewheel option
            FlickityCarousel.prototype.initCarousel = function() {
                // Call original method
                originalInitCarousel.call(this);
                
                // Add mousewheel option to the options object
                if (!this.options.hasOwnProperty('mousewheel')) {
                    this.options.mousewheel = false;
                }
                
                // Check for data attribute to enable mousewheel
                if (this.carousel && this.carousel.hasAttribute('data-mousewheel')) {
                    this.options.mousewheel = true;
                }
                
                // Initialize mousewheel if enabled
                if (this.options.mousewheel && this.carouselInstance) {
                    this.carouselInstance._initMousewheel();
                }
            };
        }
    }

    console.log('Flickity Mousewheel Extension loaded successfully');

})(); 